import { useEffect, useState, useCallback } from 'react'
import api from '../services/api'

/**
 * AdminHandover — super-admin cross-tenant view of the Human Handover
 * feature. Lets the operator see:
 *   - every Customer with sub-user agent counts
 *   - live handover counts (waiting / live / ended-today)
 *   - handover_enabled toggle (admin override)
 *   - drilldown into one customer's agents + full handover history
 */
export default function AdminHandover() {
  const [data, setData] = useState(null)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [openCustomer, setOpenCustomer] = useState(null)
  const [openData, setOpenData] = useState(null)
  const [togglingId, setTogglingId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const params = {}
      if (search) params.search = search
      const r = await api.get('/admin/handover/customers', { params })
      setData(r.data)
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load.')
    } finally { setLoading(false) }
  }, [search])

  const openDrill = async (customerId) => {
    setOpenCustomer(customerId); setOpenData(null)
    try {
      const r = await api.get(`/admin/handover/customers/${customerId}`)
      setOpenData(r.data)
    } catch (e) {
      setError(e?.response?.data?.detail || 'Drilldown failed.')
    }
  }

  const toggle = async (customerId, enabled) => {
    setTogglingId(customerId)
    try {
      await api.put(`/admin/handover/customers/${customerId}/toggle`, { enabled })
      load()
      if (openCustomer === customerId) openDrill(customerId)
    } catch (e) {
      alert(e?.response?.data?.detail || 'Toggle failed.')
    } finally { setTogglingId(null) }
  }

  useEffect(() => { load() }, [load])

  if (loading && !data) return <div className="p-6">Loading…</div>

  const t = data?.totals || {}
  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Human Handover</h1>
        <p className="text-sm text-slate-500 mt-1">All customers, agent sub-users, and live handover activity.</p>
      </div>

      {error && <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Kpi label="Customers" value={t.customers ?? 0} tone="slate" />
        <Kpi label="Enabled" value={t.handover_enabled_customers ?? 0} tone="indigo" />
        <Kpi label="Total Agents" value={t.total_agents ?? 0} tone="emerald" />
        <Kpi label="Active Agents" value={t.active_agents ?? 0} tone="emerald" />
        <Kpi label="Waiting" value={t.waiting_handovers ?? 0} tone="amber" />
        <Kpi label="Live" value={t.live_handovers ?? 0} tone="indigo" />
      </div>

      <div className="flex items-center justify-between">
        <input
          placeholder="Search by email, name, or company…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-80 px-3 py-2 text-sm border border-slate-300 rounded"
        />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Customer</th>
              <th className="px-4 py-3 text-left">Plan</th>
              <th className="px-4 py-3 text-left">Agents</th>
              <th className="px-4 py-3 text-left">Waiting</th>
              <th className="px-4 py-3 text-left">Live</th>
              <th className="px-4 py-3 text-left">Ended Today</th>
              <th className="px-4 py-3 text-left">Enabled</th>
              <th className="px-4 py-3 text-left"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data?.customers || []).map((c) => (
              <tr key={c.id} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{c.full_name || c.email}</div>
                  <div className="text-xs text-slate-500">{c.email}{c.company ? ` · ${c.company}` : ''}</div>
                </td>
                <td className="px-4 py-3 text-slate-600">{c.subscription_plan || '—'}</td>
                <td className="px-4 py-3"><span className="text-slate-900">{c.total_agents}</span> <span className="text-xs text-slate-500">({c.active_agents} active)</span></td>
                <td className="px-4 py-3">{c.waiting_handovers > 0 ? <span className="inline-flex items-center rounded bg-amber-100 text-amber-800 px-2 py-0.5 text-xs">{c.waiting_handovers}</span> : <span className="text-slate-400">0</span>}</td>
                <td className="px-4 py-3">{c.live_handovers > 0 ? <span className="inline-flex items-center rounded bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs">{c.live_handovers}</span> : <span className="text-slate-400">0</span>}</td>
                <td className="px-4 py-3 text-slate-600">{c.ended_today}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => toggle(c.id, !c.handover_enabled)}
                    disabled={togglingId === c.id}
                    aria-pressed={c.handover_enabled}
                    aria-label={`Toggle handover for ${c.email}`}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${c.handover_enabled ? 'bg-indigo-600' : 'bg-slate-300'} disabled:opacity-50`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${c.handover_enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </button>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => openDrill(c.id)} className="text-xs px-2.5 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50">View</button>
                </td>
              </tr>
            ))}
            {(!data?.customers || data.customers.length === 0) && (
              <tr><td colSpan={8} className="text-center text-slate-400 py-8">No customers found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {openCustomer && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end">
          <div className="w-full max-w-2xl h-full bg-white shadow-2xl flex flex-col">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-900">{openData?.customer?.email || '…'}</h2>
                <p className="text-xs text-slate-500">
                  {openData?.customer ? `${openData.customer.total_agents} agents · ${openData.customer.live_handovers} live · ${openData.customer.waiting_handovers} waiting` : 'loading…'}
                </p>
              </div>
              <button onClick={() => { setOpenCustomer(null); setOpenData(null) }} className="text-sm px-3 py-1.5 rounded bg-slate-900 text-white">Close</button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-6">
              {!openData ? <div className="text-sm text-slate-400">Loading…</div> : (
                <>
                  <section>
                    <h3 className="text-sm font-semibold text-slate-900 mb-2">Agents</h3>
                    {openData.agents.length === 0 ? <p className="text-xs text-slate-400">No agents.</p> : (
                      <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md">
                        {openData.agents.map((a) => (
                          <li key={a.id} className="px-3 py-2">
                            <div className="text-sm text-slate-900">{a.full_name || a.email} {a.agent_display_id && <span className="ml-2 text-xs rounded bg-slate-100 px-1.5 py-0.5">{a.agent_display_id}</span>}</div>
                            <div className="text-xs text-slate-500">{a.email} {!a.is_active && <span className="ml-2 text-rose-700 bg-rose-50 rounded px-1.5">inactive</span>}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                  <section>
                    <h3 className="text-sm font-semibold text-slate-900 mb-2">Recent handovers</h3>
                    {openData.handovers.length === 0 ? <p className="text-xs text-slate-400">No handover history.</p> : (
                      <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md max-h-[400px] overflow-y-auto">
                        {openData.handovers.map((h) => (
                          <li key={h.id} className="px-3 py-2">
                            <div className="text-sm text-slate-900 truncate">{h.first_message || <em className="text-slate-400">no preview</em>}</div>
                            <div className="text-xs text-slate-500 flex gap-2 flex-wrap mt-0.5">
                              <span className={
                                h.status === 'requested' ? 'rounded bg-amber-100 text-amber-800 px-1.5 py-0.5' :
                                h.status === 'claimed' || h.status === 'active' ? 'rounded bg-emerald-100 text-emerald-800 px-1.5 py-0.5' :
                                'rounded bg-slate-100 text-slate-700 px-1.5 py-0.5'
                              }>{h.status}</span>
                              {h.agent_display_id && <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-indigo-700">{h.agent_display_id}</span>}
                              <span>· {new Date(h.requested_at).toLocaleString()}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Kpi({ label, value, tone }) {
  const tones = {
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
  }
  return (
    <div className={`rounded-lg border p-4 ${tones[tone] || tones.slate}`}>
      <div className="text-xs uppercase tracking-wide opacity-80">{label}</div>
      <div className="mt-1 text-3xl font-semibold">{value}</div>
    </div>
  )
}
